import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTheme } from 'styled-components';

import { Box, ClickableText, Flex, Heading, Icon, Link, ProgressBar, Tag, Text, Tooltip } from 'core/components';
import Highcharts from 'core/components/Highcharts';
import Model from 'core/model/Model';
import Collection from 'core/model/Collection';
import { Table } from 'core/components/table';

import { ReactComponent as TrafficPakIcon } from 'app/assets/icons/trafficpak-icon.svg';
import { DataResolutionIndicator } from 'app/components/flowRateSampleIndicators/DataResolutionIndicators';
import DeviceLink from 'app/components/links/DeviceLink';

import { PLAN_TYPES } from 'app/util/constants';
import { calculateSampleRate } from '@kentik/ui-shared/util/sampleRate';
import { addCommas } from 'app/util/utils';
import usePlanFlowRate from 'app/util/hooks/usePlanFlowRate';

import { PopoverInteractionKind } from '@blueprintjs/core';
import TrafficPakVizHeader from './TrafficPakVizHeader';
import { PlanCardContainer, PlanCardLeftFlex, PlanCardMainContentWrapper, PlanIconBox } from '../layout';
import PlanCalloutWrapper from '../layout/PlanCalloutWrapper';
import FreeTrialCallout from '../FreeTrialCallout';
import LabelValue from '../../LabelValue';
import LoadingSkeleton from '../../../metrics/LoadingSkeleton';

/**
 * @typedef {Object} LookbackOption
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} TrafficPakPlanCardProps
 * @property {string|number} id
 * @property {string} name
 * @property {string} description
 * @property {boolean} bgp
 * @property {number} device_count
 * @property {number} max_devices
 * @property {number} max_fps
 * @property {number} [free_credits]
 * @property {number} [paid_credits]
 * @property {number} pak_fps
 * @property {number} fast_retention
 * @property {number} full_retention
 * @property {string} plan_type
 * @property {number} [trialDaysRemaining]
 * @property {{ qualitativeColors: string[] }} $colors
 * @property {{ deviceSummariesById: Record<string, { device_name?: string }|undefined> }} $devices
 * @property {() => void} openContactAccountTeamDialog
 */

// Used for DevicePak (plan_type 'edge') and FlowPak (plan_type 'flowpak') plans
/**
 * @param {TrafficPakPlanCardProps} props
 * @returns {JSX.Element}
 */
function TrafficPakPlanCard({
  id,
  name,
  description,
  bgp,
  device_count,
  max_devices,
  max_fps,
  free_credits,
  paid_credits,
  pak_fps,
  fast_retention,
  full_retention,
  plan_type,
  trialDaysRemaining,
  $colors,
  $devices,
  openContactAccountTeamDialog,
  ...rest
}) {
  const theme = useTheme();
  const isDarkMode = theme?.name === 'dark';
  const isFlowPak = plan_type === PLAN_TYPES.flowpak.code;
  const isFreeTrialPlan = name === 'Free Trial Plan';
  const bgpText = `${bgp ? 'Enabled (Partial)' : 'Disabled'}`;

  const timePeriodOptions = [
    { label: '1 Hour', value: 'PT3600S' },
    { label: '1 Day', value: 'PT86400S' },
    { label: '7 Days', value: 'PT604800S' },
    { label: '30 Days', value: 'PT2592000S' }
  ];
  const [lookback, setLookback] = useState(timePeriodOptions[2]);

  const filters = [
    {
      filterField: 'plan_id',
      operator: '=',
      filterValue: id || ''
    }
  ];

  const dimensions = isFlowPak ? ['plan_id'] : ['did', 'plan_id'];
  const merge = isFlowPak
    ? {
        aggregate: 'sum',
        dimensions: ['did']
      }
    : null;

  const {
    avgReceivedFps,
    avgStoredFps,
    combinedReceivedFlowTimeseries,
    flowSampleRate,
    hasFlowData,
    loadingPlanFlow,
    planFlowError,
    receivedFlowResultsByMeasurement,
    storedFlowTimeseries,
    storedFlowData
  } = usePlanFlowRate({
    dimensions,
    filters,
    lookback: lookback.value,
    merge,
    planId: id,
    withRawData: true,
    skipKproxy: isFlowPak
  });

  const getFlowDataTimeSeries = () => {
    // Return empty if there is no stored or received flow data
    if (!storedFlowTimeseries?.length || !combinedReceivedFlowTimeseries?.length) {
      return [];
    }

    const series = [];

    // Process Received Flow
    series.push({
      name: 'Received',
      data: combinedReceivedFlowTimeseries,
      color: $colors.qualitativeColors[0],
      lineWidth: 2,
      zIndex: 1
    });

    // Process Stored Flow
    series.push({
      name: 'Stored',
      data: storedFlowTimeseries,
      color: $colors.qualitativeColors[1],
      dashStyle: 'ShortDot',
      lineWidth: 2,
      zIndex: 1
    });

    // Plan Max FPS Series
    const planMaxSeries = storedFlowTimeseries.map((tuple) => [tuple[0], max_fps]);
    series.push({
      name: 'Max FPS',
      data: planMaxSeries,
      color: $colors.qualitativeColors[2],
      lineWidth: 2,
      zIndex: 1
    });

    return series;
  };

  const getFlowPakChartOptions = () => {
    const series = getFlowDataTimeSeries();

    return {
      chart: {
        type: 'line',
        animation: false,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%m/%d',
          week: '%m/%d',
          month: '%m/%y',
          year: '%Y'
        },
        labels: {
          style: {
            color: theme.colors.chart.legendLabels,
            fontSize: '11px'
          }
        }
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          formatter() {
            // "this" refers to highcharts context
            // eslint-disable-next-line react/no-this-in-sfc
            if (this.value >= 1000) {
              // eslint-disable-next-line react/no-this-in-sfc
              return `${this.value / 1000}k`;
            }
            // eslint-disable-next-line react/no-this-in-sfc
            return this.value;
          },
          style: {
            fontSize: '11px',
            color: theme.colors.chart.legendLabels
          }
        }
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        valueSuffix: 'FPS',
        valueDecimals: 1,
        xDateFormat: '%m/%d %H:%M'
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          fontSize: '12px',
          color: theme.colors.chart.legendLabels
        },
        symbolHeight: 8,
        symbolWidth: 8,
        symbolRadius: 0
      },
      plotOptions: {
        line: {
          animation: false,
          lineWidth: 2,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4
              }
            }
          }
        },
        area: {
          animation: false,
          marker: {
            enabled: false
          }
        },
        series: {
          turboThreshold: 0
        }
      },
      credits: {
        enabled: false
      },
      series // places series plots on chart
    };
  };

  const handleTimePeriodChange = (selectedValue) => {
    if (selectedValue) {
      const selectedOption = timePeriodOptions.find((option) => option.value === selectedValue);
      setLookback(selectedOption);
    }
  };

  const calculateFlowPercentOfMax = (flowValue) => {
    if (max_fps === 0) {
      return 0;
    }
    return ((flowValue / max_fps) * 100).toFixed(2);
  };

  const DataResolutionTooltipContent = (
    <>
      <Text as="div">Kentik provides intelligent downsampling</Text>
      <Text as="div">by dynamically adjusting data resolution</Text>
      <Text as="div">to ensure you stay below your max FPS</Text>
      <Link to="https://kb.kentik.com/docs/flow-overview#flow-sampling" blank>
        Knowledge Base
      </Link>
    </>
  );

  const renderFlowChart = () => {
    const headerLeftText = 'Utilization:';

    return (
      <Box>
        {/* Loading */}
        {loadingPlanFlow && <LoadingSkeleton height={250} width="100%" />}

        {/* Error handling */}
        {!loadingPlanFlow && planFlowError && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              showSelect
              timePeriodOptions={timePeriodOptions}
            />
            <Text as="div" muted my={3} textAlign="center">
              Error loading Plan FPS data
            </Text>
          </>
        )}

        {/* No data handling */}
        {!loadingPlanFlow && !planFlowError && !hasFlowData && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              showSelect
              timePeriodOptions={timePeriodOptions}
            />
            <Text as="div" muted textAlign="center" my={3}>
              No FPS data found in past {lookback.label} for{' '}
              <Text as="span" fontWeight="bold">
                {name}
              </Text>
            </Text>
          </>
        )}

        {/* Chart rendering */}
        {!loadingPlanFlow && !planFlowError && hasFlowData && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              showSelect
              timePeriodOptions={timePeriodOptions}
            />
            <Highcharts options={getFlowPakChartOptions()} styledMode={false} />
            <Flex justifyContent="space-between" alignItems="center" mt={1}>
              <Text fontWeight="bold">Received FPS (P95): </Text>
              <Text>
                {addCommas(avgReceivedFps.toFixed(0))} flow/s
                <Text color="muted" ml={1}>
                  {calculateFlowPercentOfMax(avgReceivedFps)}%
                </Text>
              </Text>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" mt={1}>
              <Text fontWeight="bold">Stored FPS (P95):</Text>
              <Text>
                {addCommas(avgStoredFps.toFixed(0))} flow/s
                <Text color="muted" ml={1}>
                  {calculateFlowPercentOfMax(avgStoredFps)}%
                </Text>
              </Text>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" mt={1}>
              <Text fontWeight="bold">
                Data Resolution
                <Tooltip
                  content={DataResolutionTooltipContent}
                  interactionKind={PopoverInteractionKind.HOVER}
                  hoverCloseDelay={100}
                >
                  <Icon icon="info-sign" ml="4px" size={12} />
                </Tooltip>
                :
              </Text>
              <DataResolutionIndicator flowSampleRate={flowSampleRate} showGoodResolutionIndicator />
            </Flex>
          </>
        )}
      </Box>
    );
  };

  const devicesOverMaxFpsCollection = useMemo(() => {
    if (!storedFlowData?.models?.length) {
      return new Collection([]);
    }

    const models = [];

    storedFlowData.models.forEach((storedModel) => {
      const did = storedModel.get('did');

      // Find matching models from other datasets
      const receivedModels = Object.values(receivedFlowResultsByMeasurement || {})
        .filter(Boolean)
        .map((source) => source?.models?.find((m) => m.get('did') === did))
        .filter(Boolean);

      const receivedFps = receivedModels.reduce((sum, model) => sum + (model?.get('p95_in') || 0), 0);
      const storedFps = storedModel.get('p95_out') || 0;
      const deviceFlowSampleRate = calculateSampleRate(receivedFps, storedFps);

      // Need to get the device name so we can provide it to DeviceLink
      const fullDevice = $devices.deviceSummariesById[did];

      // Only include devices over max FPS (i.e., downsampled)
      if (receivedFps > max_fps) {
        // Create a new DeviceFlowModel instance with the data
        const deviceModel = new Model({
          deviceName: fullDevice?.device_name || did,
          receivedFps,
          storedFps,
          flowSampleRate: deviceFlowSampleRate
        });

        models.push(deviceModel);
      }
    });

    const sortState = { field: 'flowSampleRate', direction: 'desc' };
    // Create and return the collection with initial sort by flowSampleRate (descending)
    const collection = new Collection(models, { sortState });

    return collection;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedFlowResultsByMeasurement, storedFlowData]);

  const fpsTableValueRenderer = ({ value }) => {
    const displayValue = value && Number.isFinite(value) ? addCommas(value.toFixed(0)) : value;
    return <Text>{displayValue}</Text>;
  };

  const renderDownsampledDevicesTable = () => {
    const headerLeftText = 'Devices Over Max FPS:';

    const columns = [
      {
        label: 'Device',
        key: 'deviceName',
        name: 'deviceName',
        width: '30%',
        renderer: ({ value }) => <DeviceLink name={value} />
      },
      {
        label: 'Received FPS',
        key: 'receivedFps',
        name: 'receivedFps',
        width: '20%',
        renderer: fpsTableValueRenderer
      },
      {
        label: 'Stored FPS',
        key: 'storedFps',
        name: 'storedFps',
        width: '20%',
        renderer: fpsTableValueRenderer
      },
      {
        label: 'Data Resolution',
        key: 'flowSampleRate',
        name: 'flowSampleRate',
        width: '30%',
        renderer: ({ value }) => <DataResolutionIndicator flowSampleRate={value} showGoodResolutionIndicator />
      }
    ];

    return (
      <Box>
        {/* Loading */}
        {loadingPlanFlow && <LoadingSkeleton height={250} width="100%" />}

        {/* Error handling */}
        {!loadingPlanFlow && planFlowError && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              timePeriodOptions={timePeriodOptions}
            />
            <Text as="div" muted my={3} textAlign="center">
              Error loading Plan FPS data
            </Text>
          </>
        )}

        {/* No data handling */}
        {!loadingPlanFlow && !planFlowError && !hasFlowData && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              timePeriodOptions={timePeriodOptions}
            />
            <Text as="div" muted textAlign="center" my={3}>
              No FPS data found in past {lookback.label} for{' '}
              <Text as="span" fontWeight="bold">
                {name}
              </Text>
            </Text>
          </>
        )}

        {/* Chart rendering */}
        {!loadingPlanFlow && !planFlowError && hasFlowData && (
          <>
            <TrafficPakVizHeader
              id={id}
              leftText={headerLeftText}
              lookback={lookback}
              handleTimePeriodChange={handleTimePeriodChange}
              timePeriodOptions={timePeriodOptions}
            />
            <Box border="thin" borderRadius={4} mt={2} p="4px" borderColor={isDarkMode ? 'muted' : 'dark'}>
              <Table
                collection={devicesOverMaxFpsCollection}
                columns={columns}
                loading={loadingPlanFlow}
                stickyHeader
              />
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <PlanCardContainer {...rest}>
      <PlanCardLeftFlex>
        <PlanIconBox>
          <TrafficPakIcon style={{ width: 40, height: 40 }} />
        </PlanIconBox>

        <PlanCardMainContentWrapper>
          <Text color="muted" mb="4px" fontWeight={500}>
            Traffic
          </Text>
          <Tag ml={1}>{isFlowPak ? 'FlowPak' : 'DevicePak'}</Tag>

          {/* Plan Name & ID */}
          <Heading level={4} mb={1} mt={1}>
            {name}{' '}
            <Text fontWeight={200} muted>
              ({id})
            </Text>
          </Heading>

          {/* Plan Description */}
          <Text color="muted" as="div" mb={1}>
            {description}
          </Text>

          {/* Device Count + Progress Bar */}
          {/* width={520} */}
          <Box mt="4px">
            <Box mb={1}>
              <ProgressBar
                animate={false}
                stripes={false}
                value={device_count / max_devices}
                intent={device_count >= max_devices ? 'danger' : 'primary'}
              />
            </Box>
            <Flex justifyContent="space-between">
              <Text fontWeight="bold">Devices: </Text>
              <Text color="muted">
                {device_count}
                {' of '}
                {max_devices} devices
              </Text>
            </Flex>
          </Box>
          <Box mt={3} width="100%">
            {isFlowPak ? renderFlowChart() : renderDownsampledDevicesTable()}
          </Box>
        </PlanCardMainContentWrapper>
      </PlanCardLeftFlex>
      <Box>
        <Text color="muted" as="div" fontWeight="normal" mb={1}>
          Entitlements
        </Text>

        <LabelValue
          label={isFlowPak ? 'Max FPS' : 'Max Devices'}
          value={isFlowPak ? addCommas(max_fps) : `${max_devices}`}
        />

        <LabelValue
          label={isFlowPak ? 'Devices' : 'Max FPS Per Device'}
          value={isFlowPak ? `${device_count} of ${max_devices}` : addCommas(max_fps)}
        />

        <LabelValue label="BGP" value={bgpText} />
        <LabelValue label="Fast Retention" value={`${fast_retention} days`} />
        <LabelValue label="Full Retention" value={`${full_retention} days`} mb={0} />

        {/* Call Outs */}
        {isFreeTrialPlan && <FreeTrialCallout trialDaysRemaining={trialDaysRemaining} />}

        {!isFlowPak && (
          <>
            {device_count >= max_devices && (
              <PlanCalloutWrapper icon="cross-circle" intent="danger">
                You have reached your device limit.
                <ClickableText onClick={openContactAccountTeamDialog}>Please contact your account team.</ClickableText>
              </PlanCalloutWrapper>
            )}

            {max_devices - device_count <= 2 && device_count < max_devices && (
              <PlanCalloutWrapper icon="warning-sign" intent="warning">
                You are nearing your device limit.
                <ClickableText onClick={openContactAccountTeamDialog}>
                  Please contact your account team if needed to increase your limit.
                </ClickableText>
              </PlanCalloutWrapper>
            )}

            {devicesOverMaxFpsCollection.size > 0 && (
              <PlanCalloutWrapper icon="warning-sign" intent="warning">
                {devicesOverMaxFpsCollection.size} devices are currently over their max FPS.{' '}
                <ClickableText onClick={openContactAccountTeamDialog}>
                  Please contact your account team or adjust device sampling.
                </ClickableText>
              </PlanCalloutWrapper>
            )}
          </>
        )}

        {isFlowPak && (
          <>
            {avgReceivedFps > max_fps && (
              <PlanCalloutWrapper icon="cross-circle" intent="danger">
                Received FPS is very high for your current plan.
                <ClickableText onClick={openContactAccountTeamDialog}>Please contact your account team.</ClickableText>
              </PlanCalloutWrapper>
            )}
          </>
        )}
      </Box>
    </PlanCardContainer>
  );
}

export default inject('$colors', '$devices')(observer(TrafficPakPlanCard));
